/**
 * Feedback API — the public popup on the marketing site (PROJECT-289 T0289.9).
 *
 * **PUBLIC ROUTES: `anonymous: true`, NO Authorization header at all.** This is the estate's only
 * unauthenticated write, which is why the route carries a rate limit rather than a token — a
 * visitor on the landing page has no account and asking for one to leave a comment would lose the
 * comment.
 *
 * ⚠️ **THE DROPDOWN'S OPTIONS COME FROM THE SERVER (`/feedback/sections`), NOT FROM A LIST HERE.**
 * The section ids are the CONTRACT: they land in `feedback.section` and become the subject a
 * promoted request is keyed to, and they are also what the route validates against. A second copy
 * in this file would disagree with the server the first time a section is added — and the failure
 * would be "the new option is silently refused with a 400", which is exactly the kind of breakage a
 * duplicated vocabulary produces. So this module fetches them and renders what it is given.
 */

import { apiClient, ApiClientError } from "./apiClient";

export interface FeedbackSection {
  id: string;
  label: string;
}

/** The visitor's own two answers. `-1` negative · `0` neutral · `1` positive. */
export type FeedbackEmotion = -1 | 0 | 1;

export interface FeedbackSubmission {
  /** Omit BOTH `emotion` and `rating` for a bare comment — the server refuses half a pair, because
   *  the promotion trigger reads these as the visitor's answer and half of one cannot be read. */
  emotion?: FeedbackEmotion;
  rating?: number;
  message?: string;
  section: string;
  page_url?: string;
  posthog_distinct_id?: string;
}

interface SubmitResponse {
  status: boolean;
  statusCode: number;
  message: string;
  data?: { id: string; section: string } | null;
}

interface SectionsResponse {
  status: boolean;
  statusCode: number;
  message: string;
  data: { sections: FeedbackSection[] } | null;
}

/** The dropdown's options FOR THIS PAGE. Fetched when the panel opens, not on page load — the
 *  visitor may never open it, and a marketing page should not spend a request on a panel nobody
 *  asked for.
 *
 * ⚠️ **`path` IS WHY THIS TAKES AN ARGUMENT.** The panel is mounted in `PublicLayout`, so it lives
 * on every public page, but the landing page's blocks ("The free taste", "The Raise") do not exist
 * on a blog post. The server owns which list a path gets (`sections_for_path`) — the rules live
 * there, not here, for the reason the module docstring gives. Omit it and the server returns the
 * page-agnostic list rather than guessing (measured: an omitted `path` used to be indistinguishable
 * from the landing page, which is how a blog post came to offer the hero). */
export async function fetchFeedbackSections(path?: string): Promise<FeedbackSection[]> {
  const q = path ? `?path=${encodeURIComponent(path)}` : "";
  const res = await apiClient<SectionsResponse>(`/api/v1/feedback/sections${q}`, {
    method: "GET",
    anonymous: true,
  });
  return res.data?.sections ?? [];
}

/**
 * Send one message.
 *
 * Throws `ApiClientError` on a refusal, carrying **the server's own sentence** — which is why the
 * caller shows `err.message` rather than composing its own. The server distinguishes "half a
 * signal" from "rate limited" from "unknown section" in wording a visitor can act on, and a
 * client-side paraphrase would flatten those into one unhelpful line.
 */
export async function submitFeedback(
  submission: FeedbackSubmission,
): Promise<SubmitResponse> {
  return apiClient<SubmitResponse>("/api/v1/feedback", {
    method: "POST",
    anonymous: true,
    body: JSON.stringify(submission),
  });
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE READER — the admin half, and NOT anonymous
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
// ⚠️ **EVERYTHING ABOVE THIS LINE IS PUBLIC; THIS IS NOT.** The two halves live in one file because
// they are one subject (the feedback queue) and splitting them would put the section vocabulary's
// consumer in a different file from its producer. The difference is stated here rather than left to
// the reader: these calls carry the admin JWT (`apiClient` authenticates unless told otherwise) and
// the server answers **403** to a signed-in non-admin, because a visitor's words and the pages they
// came from are not public data.

export type FeedbackSignal = "negative" | "positive" | "neutral" | "comment";

/** The request a message filed, or `null` — which is a real answer, not missing data: a positive
 *  message files nothing by design (`D-289-008` promotes negatives only), and a negative one whose
 *  bridge failed is exactly what an admin needs to be able to see. */
export interface FeedbackRequestRef {
  id: string;
  kind: string | null;
  status: string;
  /** How many arrivals the queue counted on that row — the same number `resolution_notes` carries,
   *  read through the function that owns the marker so no client parses that prose. */
  occurrences: number;
}

export interface FeedbackMessageData {
  id: string;
  createdAt: string | null;
  signal: FeedbackSignal;
  emotion: number | null;
  rating: number | null;
  message: string | null;
  /** The dropdown answer — a landing-page block id, or a page-agnostic one. */
  section: string | null;
  /** The visitor's words for that block, from the server's own tuple — `free-taste` becomes
   *  "The free taste". `null` when the id is unknown to the server's list. */
  sectionLabel: string | null;
  /** The page it was sent FROM, which the section alone cannot tell you once one section id exists
   *  on more than one page. */
  pageUrl: string | null;
  entityId: string | null;
  request: FeedbackRequestRef | null;
}

export interface ListFeedbackParams {
  page?: number;
  pageSize?: number;
  /** Omitted returns every message. The server refuses an unknown value (400) rather than
   *  returning an empty list, so a typo cannot read as "nobody has written to us". */
  signal?: FeedbackSignal | "";
}

interface FeedbackListResponse {
  status: boolean;
  statusCode: number;
  message: string;
  data: {
    messages: FeedbackMessageData[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } | null;
}

/** Every message the panel captured, newest first, each beside the request it filed.
 *
 * PROJECT-289 — the reader. Measured 2026-10-11, nothing in the estate read this table: the capture
 * wrote rows and no surface presented one to a person, which is the defect (`@P289` D-289-009) the
 * project was started to avoid. */
export async function listFeedbackMessages(
  params?: ListFeedbackParams,
): Promise<FeedbackListResponse> {
  const p = new URLSearchParams();
  p.set("page", String(params?.page ?? 1));
  p.set("pageSize", String(params?.pageSize ?? 20));
  if (params?.signal) p.set("signal", params.signal);
  return apiClient<FeedbackListResponse>(`/api/v1/feedback/list?${p.toString()}`, {
    method: "GET",
  });
}

export { ApiClientError };
